import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "src/common/utils";
import { InvalidFieldTooltip } from "src/components/ui/invalid-field-tooltip";

import type { DigestSection } from "../CreateDigestWizard/types";

const SECTION_EXAMPLES = [
  { name: "Error Analysis", prompt: "Check error rates across all services over the last 24 hours. Flag anything above 1% and summarize the most frequent error types." },
  { name: "Deployment Summary", prompt: "List all deployments in the last 24 hours. Note any rollbacks, failed deploys, or services with multiple redeploys." },
  { name: "Infrastructure Health", prompt: "Review CPU, memory, and disk usage across production hosts. Highlight any resources trending toward capacity limits." },
  { name: "On-Call Recap", prompt: "Summarize all alerts that fired in the last 24 hours. Group by service and note which were acknowledged vs auto-resolved." },
];

interface CompactSectionBuilderProps {
  value: string;
  onChange: (value: string) => void;
  onError?: (error: boolean) => void;
  showValidationErrors?: boolean;
}

function SortableSectionRow({
  section,
  index,
  isMultiple,
  showValidationErrors,
  onSectionChange,
  onRemove,
}: {
  section: DigestSection;
  index: number;
  isMultiple: boolean;
  showValidationErrors: boolean;
  onSectionChange: (index: number, field: "section" | "text", value: string) => void;
  onRemove: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const nameRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const nameInvalid = showValidationErrors && section.section.trim() === "";
  const textInvalid = showValidationErrors && section.text.trim() === "";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  };

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "0";
      el.style.height = Math.max(36, el.scrollHeight) + "px";
    }
  }, [section.text]);

  return (
    <div ref={setNodeRef} style={style} className="group rounded-lg border border-border bg-card/30 overflow-hidden">
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border/50 bg-card/50">
        <button
          type="button"
          aria-label="Reorder section"
          className="cursor-grab p-0.5 rounded hover:bg-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 flex-shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical size={12} className="text-muted-foreground" />
        </button>
        <label
          htmlFor={`section-name-${section.id}`}
          className="text-xs text-muted-foreground flex-shrink-0 cursor-text select-none"
        >
          Section {index + 1} ·
        </label>
        <InvalidFieldTooltip invalid={nameInvalid} message="Section name is required">
          <input
            ref={nameRef}
            id={`section-name-${section.id}`}
            type="text"
            value={section.section}
            onChange={(e) => onSectionChange(index, "section", e.target.value)}
            placeholder={index < SECTION_EXAMPLES.length ? SECTION_EXAMPLES[index].name : `Section ${index + 1} name`}
            aria-invalid={nameInvalid}
            className={cn(
              "flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:rounded-sm min-w-0",
              nameInvalid && "ring-1 ring-red-500 rounded-sm placeholder:text-red-500/60",
            )}
          />
        </InvalidFieldTooltip>
        {isMultiple && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            aria-label="Remove section"
            className="p-0.5 rounded text-muted-foreground/50 hover:text-red-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/30 flex-shrink-0"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
      <InvalidFieldTooltip invalid={textInvalid} message="Section prompt is required">
        <textarea
          ref={textareaRef}
          value={section.text}
          onChange={(e) => onSectionChange(index, "text", e.target.value)}
          placeholder={index < SECTION_EXAMPLES.length ? SECTION_EXAMPLES[index].prompt : "Describe what this section should cover..."}
          aria-label={`Section ${index + 1} prompt`}
          aria-invalid={textInvalid}
          rows={1}
          className={cn(
            "w-full px-3 py-2 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/40 resize-none focus:outline-none focus:ring-1 focus:ring-primary/30",
            textInvalid && "ring-1 ring-inset ring-red-500 placeholder:text-red-500/60",
          )}
        />
      </InvalidFieldTooltip>
    </div>
  );
}

export function CompactSectionBuilder({ value, onChange, onError, showValidationErrors = false }: CompactSectionBuilderProps) {
  const [sections, setSections] = useState<DigestSection[]>([{ section: "", text: "", id: "1" }]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Parse incoming JSON value
  useEffect(() => {
    try {
      if (!value) return;
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      const rawSections = parsed.sections || parsed.input || (Array.isArray(parsed) ? parsed : null);
      if (rawSections && rawSections.length > 0) {
        setSections(
          rawSections.map((s: { section: string; text: string }, i: number) => ({
            section: s.section || "",
            text: s.text || "",
            id: (i + 1).toString(),
          })),
        );
      }
    } catch {
      // keep defaults
    }
  }, [value]);

  const emitChange = (newSections: DigestSection[]) => {
    setSections(newSections);
    const currentValue = typeof value === "string" ? JSON.parse(value) : value;
    const output = {
      sections: newSections.map((s) => ({ section: s.section, text: s.text })),
      slack_channel_id: currentValue?.slack_channel_id || "",
    };
    onChange(JSON.stringify(output));

    const valid = newSections.every((s) => s.section.trim() !== "" && s.text.trim() !== "");
    onError?.(!valid);
  };

  const handleSectionChange = (index: number, field: "section" | "text", val: string) => {
    const next = [...sections];
    next[index][field] = val;
    emitChange(next);
  };

  const addSection = () => {
    const newId = (Math.max(...sections.map((s) => parseInt(s.id)), 0) + 1).toString();
    emitChange([...sections, { section: "", text: "", id: newId }]);
  };

  const removeSection = (index: number) => {
    if (sections.length <= 1) return;
    emitChange(sections.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      emitChange(arrayMove(sections, oldIndex, newIndex));
    }
  };

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sections.map((section, index) => (
              <SortableSectionRow
                key={section.id}
                section={section}
                index={index}
                isMultiple={sections.length > 1}
                showValidationErrors={showValidationErrors}
                onSectionChange={handleSectionChange}
                onRemove={removeSection}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={addSection}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors pl-0.5 py-2"
      >
        <Plus size={11} />
        Add section
      </button>
    </div>
  );
}

export default CompactSectionBuilder;
